import {useMemo, useState} from "react";
import {TagDefinitionEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";
import {getFormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";

export type UseTagDefinitionDataTableFilterProps = Omit<UseDataTableFilterProps<TagDefinitionEntity>, 'filterConfig'>

export function useTagDefinitionDataTableFilter(props: UseTagDefinitionDataTableFilterProps): UseDataTableFilterResult<TagDefinitionEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [labelQuery, setLabelQuery] = useState<string | undefined>(undefined);
  const [labelDebouncedQuery] = useDebouncedValue(labelQuery, 200);

  const [sourceKindQuery, setSourceKindQuery] = useState<string | undefined>(undefined);
  const [sourceKindDebouncedQuery] = useDebouncedValue(sourceKindQuery, 200);

  const filterConfig: DataTableFilterConfig<TagDefinitionEntity> = useMemo(() => {
    return {
      "label": {
        setQuery: setLabelQuery,
        query: labelQuery,
        debouncedQuery: labelDebouncedQuery,
        filter: ({label}, query) => !label || !label.toLowerCase().includes(query.toLowerCase())
      },
      "sourceKind": {
        setQuery: setSourceKindQuery,
        query: sourceKindQuery,
        debouncedQuery: sourceKindDebouncedQuery,
        filter: ({sourceKind}, query) => {
          if (sourceKind) {
            const display = getFormattedDatabaseCode(sourceKind, t)
            return !sourceKind.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase()))
          } else {
            return false
          }
        }
      },
    }
  }, [
    labelQuery,
    labelDebouncedQuery,
    sourceKindQuery,
    sourceKindDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
